import {
  NAME_MAX,
  NAME_MIN,
  NAME_PATTERN,
} from "../SpecialityTreatment/specialityTreatmentForm";

export const MAX_PROCEDURE_OPTIONS = 100;

const NAME_CHARS_HINT = "Only letters, numbers, spaces and - / & ( ) , . ' + are allowed";

const idOf = (value) => (value && typeof value === "object" ? value._id : value);
const lower = (value) => String(value ?? "").trim().toLowerCase();

export const CUSTOM_SOURCE = "CUSTOM";
export const isCustomTreatment = (item) =>
  item?.custom === true || item?.source === CUSTOM_SOURCE;

export const PROCEDURE_SHEET_FILES = Object.freeze({
  template: "procedure_master_template.xlsx",
  export: "procedure_master_export.xlsx",
  columns: Object.freeze([
    "Speciality",
    "Treatment Category *",
    "Linked Treatments *",
    "Procedure Options *",
    "Status",
  ]),
});

export const validateLinkedTreatmentName = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "Linked treatment is required";
  if (trimmed.length < NAME_MIN) {
    return `Linked treatment must be at least ${NAME_MIN} characters`;
  }
  if (trimmed.length > NAME_MAX) {
    return `Linked treatment must be at most ${NAME_MAX} characters`;
  }
  if (!NAME_PATTERN.test(trimmed)) return `Linked treatment: ${NAME_CHARS_HINT}`;
  return "";
};

export const addLinkedTreatment = (linked, raw, category) => {
  const current = Array.isArray(linked) ? linked : [];
  const name = String(raw ?? "").trim();
  if (!name) return { linked: current, error: "" };

  const subs = Array.isArray(category?.subTreatments) ? category.subTreatments : [];
  const master = subs.find((sub) => lower(sub?.name) === name.toLowerCase());
  const taken = current.find(
    (item) => (master && String(idOf(item)) === String(master._id)) || lower(item?.name) === name.toLowerCase()
  );
  if (taken) return { linked: current, error: `"${taken.name}" is already linked` };
  if (master) {
    if (master.isActive === false) {
      return { linked: current, error: `"${master.name}" is inactive and cannot be linked` };
    }
    return { linked: [...current, { ...master }], error: "" };
  }
  const error = validateLinkedTreatmentName(name);
  if (error) return { linked: current, error };
  return {
    linked: [...current, { name, isActive: true, source: CUSTOM_SOURCE, custom: true }],
    error: "",
  };
};

export const applyLinkedTreatmentChange = (next, category) => {
  const list = Array.isArray(next) ? next : [];
  let linked = [];
  let error = "";
  list.forEach((item) => {
    if (typeof item === "string") {
      const result = addLinkedTreatment(linked, item, category);
      linked = result.linked;
      if (result.error) error = result.error;
      return;
    }
    if (!item || typeof item !== "object") return;
    const clash = linked.find(
      (seen) =>
        (item._id && String(idOf(seen)) === String(item._id)) || lower(seen?.name) === lower(item?.name)
    );
    if (clash) {
      error = `"${clash.name}" is already linked`;
      return;
    }
    linked.push(item);
  });
  return { linked, error };
};

/** The option-name dropdown: the existing names minus those already on this procedure. */
export const procedureOptionSuggestions = (names, procedureOptions) => {
  const taken = new Set(
    (Array.isArray(procedureOptions) ? procedureOptions : []).map((option) => lower(option?.name))
  );
  const seen = new Set();
  return (Array.isArray(names) ? names : [])
    .map((name) => String(name ?? "").trim())
    .filter((name) => {
      const key = name.toLowerCase();
      if (!key || taken.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const validateProcedureOptionName = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "Procedure option is required";
  if (trimmed.length < NAME_MIN) {
    return `Procedure option must be at least ${NAME_MIN} characters`;
  }
  if (trimmed.length > NAME_MAX) {
    return `Procedure option must be at most ${NAME_MAX} characters`;
  }
  if (!NAME_PATTERN.test(trimmed)) return `Procedure option: ${NAME_CHARS_HINT}`;
  return "";
};

export const addProcedureOption = (options, raw) => {
  const current = Array.isArray(options) ? options : [];
  const name = String(raw ?? "").trim();
  if (!name) return { options: current, error: "" };

  const existing = current.find((option) => lower(option?.name) === name.toLowerCase());
  if (existing) return { options: current, error: `"${existing.name}" is already added` };

  const error = validateProcedureOptionName(name);
  if (error) return { options: current, error };

  if (current.length >= MAX_PROCEDURE_OPTIONS) {
    return {
      options: current,
      error: `A procedure can have at most ${MAX_PROCEDURE_OPTIONS} options`,
    };
  }

  return { options: [...current, { name }], error: "" };
};

export const applyProcedureOptionChange = (next) => {
  const list = Array.isArray(next) ? next : [];
  let options = list.filter((item) => item && typeof item === "object");
  let error = "";
  list
    .filter((item) => typeof item === "string")
    .forEach((text) => {
      const result = addProcedureOption(options, text);
      options = result.options;
      if (result.error) error = result.error;
    });
  return { options, error };
};

/** Keeps the linked treatments that belong to the (new) category, and every typed one. */
export const pruneLinkedTreatments = (selected, category) => {
  const allowed = new Set(
    (Array.isArray(category?.subTreatments) ? category.subTreatments : []).map((sub) =>
      String(sub._id)
    )
  );
  return (Array.isArray(selected) ? selected : []).filter(
    (item) => isCustomTreatment(item) || allowed.has(String(idOf(item)))
  );
};

export const categoryOptionsOf = (categories, rowCategory) => {
  const list = (Array.isArray(categories) ? categories : []).filter((item) => item?._id);
  if (rowCategory?._id && !list.some((item) => String(item._id) === String(rowCategory._id))) {
    list.push({ ...rowCategory, subTreatments: [] });
  }
  return list.slice().sort((a, b) => {
    const bySpeciality = String(a?.speciality?.name ?? "").localeCompare(
      String(b?.speciality?.name ?? "")
    );
    return bySpeciality || String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
  });
};

/** The dropdown: the category's treatments plus linked master ones it no longer lists; typed ones are chips only. */
export const linkedTreatmentOptionsOf = (category, selected) => {
  const options = Array.isArray(category?.subTreatments) ? category.subTreatments.slice() : [];
  (Array.isArray(selected) ? selected : []).forEach((item) => {
    if (isCustomTreatment(item)) return;
    if (!options.some((option) => String(option._id) === String(item._id))) {
      options.push(item);
    }
  });
  return options;
};

export const linkedTreatmentLabel = (item) =>
  `${item?.name ?? ""}${
    isCustomTreatment(item) ? " (custom)" : item?.isActive === false ? " (inactive)" : ""
  }`;

/** Initial editor state from a stored row (or an empty form for a new one). */
export const procedureFormOf = (row) => ({
  categoryId: row?.category?._id || "",
  linkedTreatments: (Array.isArray(row?.linkedTreatments) ? row.linkedTreatments : []).map(
    (item) => ({
      _id: item._id,
      name: item.name || "",
      isActive: item.isActive !== false,
      ...(isCustomTreatment(item) ? { source: CUSTOM_SOURCE, custom: true } : {}),
    })
  ),
  procedureOptions: (Array.isArray(row?.procedureOptions) ? row.procedureOptions : [])
    .slice()
    .sort((a, b) => (Number(a?.displayOrder) || 0) - (Number(b?.displayOrder) || 0))
    .map((option) => ({ _id: option._id, name: option.name || "" })),
});

export const validateProcedureForm = ({ category, linkedTreatments, procedureOptions }) => {
  if (!idOf(category)) return "Category is required";

  if (!(Array.isArray(linkedTreatments) && linkedTreatments.length)) {
    return "Select at least one linked treatment";
  }

  const options = Array.isArray(procedureOptions) ? procedureOptions : [];
  if (!options.length) return "Add at least one procedure option";
  if (options.length > MAX_PROCEDURE_OPTIONS) {
    return `A procedure can have at most ${MAX_PROCEDURE_OPTIONS} options`;
  }

  const seen = new Set();
  for (const option of options) {
    const error = validateProcedureOptionName(option?.name);
    if (error) return error;
    const key = lower(option.name);
    if (seen.has(key)) return `Duplicate procedure option: ${String(option.name).trim()}`;
    seen.add(key);
  }

  return "";
};

export const buildProcedurePayload = ({
  category,
  linkedTreatments = [],
  procedureOptions = [],
  original,
}) => {
  const savedIds = new Map();
  (Array.isArray(original?.procedureOptions) ? original.procedureOptions : []).forEach(
    (option) => {
      if (option?._id && !savedIds.has(lower(option.name))) {
        savedIds.set(lower(option.name), option._id);
      }
    }
  );

  const linked = Array.isArray(linkedTreatments) ? linkedTreatments : [];
  return {
    category: idOf(category),
    subTreatmentIds: linked.filter((item) => !isCustomTreatment(item)).map(idOf),
    customTreatments: linked.filter(isCustomTreatment).map((item) => {
      const name = String(item?.name ?? "").trim();
      return item?._id ? { _id: item._id, name } : { name };
    }),
    procedureOptions: (Array.isArray(procedureOptions) ? procedureOptions : []).map((option) => {
      const name = String(option?.name ?? "").trim();
      const _id = savedIds.get(name.toLowerCase()) || option?._id;
      return _id ? { _id, name } : { name };
    }),
  };
};

export const linkedTreatmentsText = (row) =>
  (Array.isArray(row?.linkedTreatments) ? row.linkedTreatments : [])
    .map(linkedTreatmentLabel)
    .join("; ");

export const procedureOptionsText = (row) =>
  (Array.isArray(row?.procedureOptions) ? row.procedureOptions : [])
    .map((option) => option?.name ?? "")
    .join("; ");

/** Accessible name for a row's action buttons. */
export const procedureRowLabel = (row) => {
  const treatments = (Array.isArray(row?.linkedTreatments) ? row.linkedTreatments : [])
    .map((item) => item?.name)
    .filter(Boolean)
    .join(", ");
  const category = row?.category?.name || "procedure";
  return treatments ? `${category} (${treatments})` : category;
};
